// src/pages/PendingUsersPage.jsx
import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  HStack,
  VStack,
  Button,
  Badge,
  Spinner,
  Icon,
} from "@chakra-ui/react";

import { useColorMode } from "../components/ui/color-mode";
import { CheckCircle, XCircle, Lock   } from "lucide-react";
import AuthContext from "../context/AuthContext";
import { showSuccess, showError } from "../utils/toastUtils";

export default function PendingUsersPage() {
  const { user } = useContext(AuthContext);
  const { colorMode } = useColorMode();

  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const hasAccess =
    user?.role === "ROLE_ADMIN" && ["HIGH", "GLOBAL"].includes(user?.adminLevel);

  const cardBg = colorMode === "light" ? "white" : "gray.800";
  const borderColor = colorMode === "light" ? "gray.200" : "whiteAlpha.300";
  const titleColor = colorMode === "light" ? "blue.600" : "blue.300";
  const subtleText = colorMode === "light" ? "gray.600" : "gray.300";

  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`);
      const filtered = (res?.data || []).filter((u) => !u.enabled);
      setPendingUsers(filtered);
    } catch (err) {
      console.error("Fehler beim Laden der Benutzer:", err);
      showError("Fehler!", "Fehler beim Laden der Benutzer.");
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (id) => {
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/api/users/approve/${id}`);
      showSuccess("✅ Benutzer freigeschaltet");
      fetchPendingUsers();
    } catch (err) {
      console.error("Fehler beim Freischalten:", err);
      showError("Fehler!", "Fehler beim Freischalten.");
    }
  };

  const rejectUser = async (id) => {
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/api/users/reject/${id}`);
      showSuccess("❌ Benutzer abgelehnt");
      fetchPendingUsers();
    } catch (err) {
      console.error("Fehler beim Ablehnen:", err);
      showError("Fehler!", "Fehler beim Ablehnen.");
    }
  };

  useEffect(() => {
    fetchPendingUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasAccess) {
    return (
      <Container maxW="3xl" py={10}>
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
          p={8}
          textAlign="center"
          boxShadow="md"
        >
          <Icon as={Lock} boxSize={10} color="red.500" mb={3} />
          <Heading size="md" color="red.500" mb={2}>
            Zugriff verweigert
          </Heading>
          <Text color={subtleText}>
            Diese Seite ist nur für HIGH oder GLOBAL Admins sichtbar.
          </Text>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="6xl" py={6}>
      <HStack justify="space-between" mb={4}>
        <Heading size="lg" color={titleColor}>
          📝 Ausstehende Registrierungsanfragen
        </Heading>
        {!loading && (
          <Badge colorScheme="blue" borderRadius="md" px={2}>
            {pendingUsers.length} offen
          </Badge>
        )}
      </HStack>

      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="xl"
        boxShadow="md"
        p={{ base: 4, md: 6 }}
        minH="120px"
      >
        {loading ? (
          <HStack justify="center" py={10}>
            <Spinner size="lg" thickness="4px" color="blue.500" />
          </HStack>
        ) : pendingUsers.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Text color={subtleText}>
              Keine ausstehenden Benutzerregistrierungen.
            </Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
            {pendingUsers.map((u) => (
              <UserCard
                key={u.id}
                user={u}
                onApprove={() => approveUser(u.id)}
                onReject={() => rejectUser(u.id)}
                cardBg={cardBg}
                borderColor={borderColor}
                subtleText={subtleText}
              />
            ))}
          </SimpleGrid>
        )}
      </Box>
    </Container>
  );
}

function UserCard({
  user,
  onApprove,
  onReject,
  cardBg,
  borderColor,
  subtleText,
}) {
  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      p={4}
      boxShadow="sm"
      _hover={{ boxShadow: "md", transform: "translateY(-2px)" }}
      transition="all 0.15s ease"
    >
      <VStack align="stretch" spacing={2}>
        <Heading size="sm" noOfLines={1}>
          {user.email}
        </Heading>
        <Text fontSize="sm" color={subtleText}>
          ID: {user.id}
        </Text>

        <HStack justify="flex-end" spacing={2} pt={2}>
          <Button
            size="sm"
            colorScheme="green"
            leftIcon={<CheckCircle size={16} />}
            onClick={onApprove}
          >
            Freischalten
          </Button>
          <Button
            size="sm"
            colorScheme="red"
            variant="solid"
            leftIcon={<XCircle size={16} />}
            onClick={onReject}
          >
            Ablehnen
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}